import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import AnnouncementService from '~/services/AnnouncementService';
import type { Announcement } from '~/types/api';

/**
 * Duyuru mesajlarını yönetmek için composable
 */
export function useAnnouncement() {
  const { locale } = useI18n();
  
  const announcements = ref<Announcement[]>([]);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  
  // Aktif duyuruların filtrelenmesi (tarih kontrolü)
  const activeAnnouncements = computed(() => {
    const now = new Date();
    
    return announcements.value.filter(announcement => {
      const startDate = new Date(announcement.start_date);
      const endDate = new Date(announcement.end_date);
      
      return announcement.is_active === '1' && 
             now >= startDate && 
             now <= endDate;
    });
  });
  
  // Aktif bir duyuru var mı?
  const hasActiveAnnouncements = computed(() => {
    return activeAnnouncements.value.length > 0;
  });
  
  // Duyuru servisini oluştur
  const announcementService = AnnouncementService.getInstance();
  
  // Duyuruları yükle
  const fetchAnnouncements = async () => {
    isLoading.value = true;
    error.value = null;
    
    try {
      const data = await announcementService.getActiveAnnouncements(locale.value);
      announcements.value = data;
    } catch (err) {
      console.error('Duyurular yüklenirken hata oluştu:', err);
      error.value = 'Duyurular yüklenirken bir hata oluştu.';
    } finally {
      isLoading.value = false;
    }
  };
  
  // Belirli bir duyuruyu getir
  const getAnnouncementById = async (id: string): Promise<Announcement | null> => {
    try {
      return await announcementService.getAnnouncementById(id, locale.value);
    } catch (err) {
      console.error(`ID: ${id} olan duyuru alınırken hata oluştu:`, err);
      return null;
    }
  };
  
  // Component oluşturulduğunda duyuruları yükle
  onMounted(() => {
    fetchAnnouncements();
  });
  
  // Dil değiştiğinde duyuruları yeniden yükle
  watch(() => locale.value, () => {
    fetchAnnouncements();
  });
  
  return {
    announcements,
    activeAnnouncements,
    hasActiveAnnouncements,
    isLoading,
    error,
    fetchAnnouncements,
    getAnnouncementById
  };
} 